import { useCallback, useEffect, useMemo, useState } from 'react';
import { ListFilters, type SortOption } from '../components/ListFilters';
import { ViewHeader } from '../components/ViewHeader';
import { AssigneeAvatar } from '../components/AssigneeAvatar';
import { TaskDetail } from '../components/TaskDetail';
import { useTeam } from '../context/TeamContext';
import { useAuth } from '../context/AuthContext';
import { usePersistedState } from '../hooks/usePersistedState';
import { useMe } from '../hooks/useMe';
import { useUserTeams } from '../hooks/useUserTeams';
import { filterByViewer } from '../utils/viewerFilter';
import { searchTasks } from '../utils/searchTasks';
import type { Task } from '../types';
import * as api from '../utils/apiClient';

function timeAgo(iso: string | undefined): string {
  if (!iso) return '';
  const secs = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

type CompletedSort = 'recent' | 'oldest' | 'priority' | 'alpha';
const SORT_OPTIONS: SortOption[] = [
  { value: 'recent', label: 'Recent first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'priority', label: 'Priority' },
  { value: 'alpha', label: 'Alphabetical' },
];

const PRIORITY_RANK: Record<string, number> = { high: 3, medium: 2, low: 1 };

function sortCompleted(tasks: Task[], mode: CompletedSort): Task[] {
  const copy = [...tasks];
  switch (mode) {
    case 'recent':
      return copy.sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''));
    case 'oldest':
      return copy.sort((a, b) => (a.completedAt ?? '').localeCompare(b.completedAt ?? ''));
    case 'priority':
      return copy.sort(
        (a, b) =>
          (PRIORITY_RANK[b.priority ?? 'low'] ?? 0) - (PRIORITY_RANK[a.priority ?? 'low'] ?? 0)
      );
    case 'alpha':
      return copy.sort((a, b) => a.title.localeCompare(b.title));
  }
}

export function CompletedView() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showingAll, setShowingAll] = useState(false);
  const [viewer, setViewer] = usePersistedState<string>('viewer.selected', 'self');
  const [sortMode, setSortMode] = usePersistedState<CompletedSort>('sort.completed', 'recent');
  const { byId } = useTeam();
  const { me } = useMe();
  const { user } = useAuth();
  const { userTeams } = useUserTeams();

  const refresh = useCallback(async (includeAll: boolean) => {
    setLoading(true);
    try {
      setTasks(await api.listTasks('completed', { all: includeAll }));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh(showingAll);
  }, [refresh, showingAll]);

  const viewerFiltered = useMemo(
    () => filterByViewer(tasks, viewer, { mySub: me?.sub ?? user?.userId ?? null, userTeams }),
    [tasks, viewer, me, user, userTeams]
  );
  const searched = useMemo(() => searchTasks(viewerFiltered, query), [viewerFiltered, query]);
  const sorted = useMemo(() => sortCompleted(searched, sortMode), [searched, sortMode]);

  const selected = useMemo(
    () => sorted.find((t) => t.taskId === selectedId) ?? null,
    [sorted, selectedId]
  );

  // TaskDetail callbacks — reuse the standard task APIs. When the caller
  // re-opens a completed task (status='open') or deletes it, drop it from
  // the local list so the view reflects the change immediately.
  const onUpdate = useCallback(
    async (patch: Parameters<React.ComponentProps<typeof TaskDetail>['onUpdate']>[0]) => {
      if (!selected) return;
      const updated = await api.updateTask(selected.teamId, selected.taskId, patch);
      if (updated.status !== 'completed' || updated.deletedAt) {
        setTasks((prev) => prev.filter((t) => t.taskId !== selected.taskId));
        setSelectedId(null);
      } else {
        setTasks((prev) => prev.map((t) => (t.taskId === selected.taskId ? updated : t)));
      }
    },
    [selected]
  );

  const onMove = useCallback(
    async (toTeamId: string) => {
      if (!selected) return;
      await api.moveTask(selected.teamId, selected.taskId, toTeamId);
      // Task moved to a different partition but stayed completed — leave it
      // in the list with an updated teamId. Cheapest path: refetch.
      await refresh(showingAll);
    },
    [selected, refresh, showingAll]
  );

  const onDelete = useCallback(async () => {
    if (!selected) return;
    await api.deleteTask(selected.teamId, selected.taskId);
    setTasks((prev) => prev.filter((t) => t.taskId !== selected.taskId));
    setSelectedId(null);
  }, [selected]);

  const onAddNote = useCallback(
    async (body: string) => {
      if (!selected) return;
      const updated = await api.addNote(selected.teamId, selected.taskId, body);
      setTasks((prev) => prev.map((t) => (t.taskId === selected.taskId ? updated : t)));
    },
    [selected]
  );

  return (
    <div className="scope-view">
      <ViewHeader title="Completed" searchValue={query} onSearchChange={setQuery} />
      <div className={`pane-wrap ${selected ? 'has-selection' : ''}`}>
        <section className="list-pane" aria-label="Completed tasks">
          {loading ? (
            <div className="muted">Loading…</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : (
            <>
              <ListFilters
                sortMode={sortMode}
                onSortChange={(m) => setSortMode(m as CompletedSort)}
                sortOptions={SORT_OPTIONS}
                viewer={viewer}
                onViewerChange={setViewer}
              />
              {sorted.length === 0 ? (
                <div className="task-list-empty">No completions to show.</div>
              ) : (
                <div className="task-list" role="list">
                  {sorted.map((task) => {
                    const groupColor =
                      me?.groups.find((g) => g.id === task.teamId)?.color ?? 'transparent';
                    const assignee = byId(task.assigneeId ?? null);
                    const isSelected = task.taskId === selectedId;
                    return (
                      <div
                        key={task.taskId}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedId(task.taskId)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedId(task.taskId);
                          }
                        }}
                        className={`task-row is-completed ${isSelected ? 'is-selected' : ''}`}
                        style={{
                          ['--row-accent' as string]: groupColor,
                          borderLeft: `3px solid ${groupColor}`,
                        }}
                      >
                        <div />
                        <div className="task-row-body">
                          <div className="task-row-title-line">
                            <div className="task-row-title">
                              {assignee && <AssigneeAvatar member={assignee} size={18} />}
                              <span>{task.title}</span>
                            </div>
                            <span className="task-row-due">
                              completed {timeAgo(task.completedAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {!showingAll && !loading && (
                <div className="load-older">
                  <button type="button" onClick={() => setShowingAll(true)}>
                    Load older
                  </button>
                  <span className="muted"> · showing last 30 days</span>
                </div>
              )}
              {showingAll && (
                <div className="load-older muted">Showing all</div>
              )}
            </>
          )}
        </section>

        <section className="detail-pane" aria-label="Task detail">
          {selected ? (
            <TaskDetail
              task={selected}
              onUpdate={onUpdate}
              onMove={onMove}
              onDelete={onDelete}
              onAddNote={onAddNote}
              onBack={() => setSelectedId(null)}
            />
          ) : (
            <div className="muted centered">Select a completed task to review or reopen.</div>
          )}
        </section>
      </div>
    </div>
  );
}
